import { useCallback, useMemo } from 'react';
import {
  AI_MODELS,
  DEFAULT_MODEL,
  buildByokModel,
} from '../components/AiChat/constants';
import { ModelGroup, ModelProvider } from '../components/AiChat/types';
import useByokSettings from './useByokSettings';
import useUserPreferences from './useUserPreferences';

const useAiChatModels = () => {
  const { userPreferences, updateUserPreferences } = useUserPreferences();
  const { data: byokSettings, models: byokModelIds } = useByokSettings();

  const byokModels = useMemo<ModelProvider[]>(() => {
    if (!byokSettings?.configured || !byokSettings.provider) {
      return [];
    }
    const provider = byokSettings.provider;
    return byokModelIds.map((id) => buildByokModel(provider, id));
  }, [byokSettings, byokModelIds]);

  const modelGroups = useMemo<ModelGroup[]>(
    () =>
      byokModels.length > 0
        ? [
            { title: 'Your models', models: byokModels },
            { title: 'Elementary', models: AI_MODELS },
          ]
        : [{ models: AI_MODELS }],
    [byokModels],
  );

  const models = useMemo(
    () => modelGroups.flatMap((group) => group.models),
    [modelGroups],
  );

  const defaultModel =
    models.find((m) => m.modelName === byokSettings?.model) ??
    byokModels[0] ??
    DEFAULT_MODEL;

  const selectedModel =
    models.find((m) => m.modelName === userPreferences.selectedAiModel) ??
    defaultModel;

  const setSelectedModel = useCallback(
    (model: ModelProvider) =>
      updateUserPreferences('selectedAiModel', model.modelName),
    [updateUserPreferences],
  );

  return {
    modelGroups,
    models,
    selectedModel,
    setSelectedModel,
    isByokConfigured: !!byokSettings?.configured,
  };
};

export default useAiChatModels;
